import { formatReportingDate } from '../constants/reportingConfig';
import {
  ArchiveViewOptions,
  CategoryOptions,
  LanguageList,
  StatusOptions,
} from './templateConstants';

function formatDateRangeChipLabel(startDate, endDate) {
  const start = formatReportingDate(startDate);
  const end = formatReportingDate(endDate);
  if (start && end) return `Updated: ${start} – ${end}`;
  if (start) return `Updated from ${start}`;
  if (end) return `Updated until ${end}`;
  return '';
}

/**
 * Derive removable active-filter chips from applied filter state.
 * Status/archived are omitted when managed by status tabs.
 */
export function getActiveFilterChips(
  filters = {},
  { excludeStatus = true, excludeArchived = true, excludeOrganisation = false } = {}
) {
  const chips = [];

  if (filters.startDate || filters.endDate) {
    chips.push({
      key: 'dateRange',
      label: formatDateRangeChipLabel(filters.startDate, filters.endDate),
      clearPatch: { startDate: null, endDate: null },
    });
  }

  if (filters.organisation && !excludeOrganisation) {
    chips.push({
      key: 'organisation',
      label: filters.organisationObj?.organisation_name
        || filters.organisationObj?.label
        || `Organisation ${filters.organisation}`,
      clearPatch: {
        organisation: '',
        organisationObj: null,
        botNumber: '',
        botNumberObj: null,
        tag_id: '',
        tagObj: null,
      },
    });
  }

  if (filters.botNumber) {
    const botLabel = filters.botNumberObj?.wa_phone_number
      || filters.botNumberObj?.label
      || filters.botNumber;
    chips.push({
      key: 'botNumber',
      label: `Bot: ${botLabel}`,
      clearPatch: { botNumber: '', botNumberObj: null },
    });
  }

  if (filters.category) {
    const categoryLabel = filters.categoryObj?.label
      || CategoryOptions.find((item) => item.value === filters.category)?.label
      || filters.category;
    chips.push({
      key: 'category',
      label: `Category: ${categoryLabel}`,
      clearPatch: { category: '', categoryObj: null },
    });
  }

  if (filters.tag_id) {
    const tagLabel = filters.tagObj?.label
      || filters.tagObj?.name
      || filters.tag_id;
    chips.push({
      key: 'tag_id',
      label: `Tag: ${tagLabel}`,
      clearPatch: { tag_id: '', tagObj: null },
    });
  }

  if (filters.language) {
    const languageLabel = filters.languageObj?.label
      || LanguageList.find((item) => item.value === filters.language)?.label
      || filters.language;
    chips.push({
      key: 'language',
      label: `Language: ${languageLabel}`,
      clearPatch: { language: '', languageObj: null },
    });
  }

  if (!excludeStatus && filters.status) {
    const statusLabel = filters.statusObj?.label
      || StatusOptions.find((item) => item.value === filters.status)?.label
      || filters.status;
    chips.push({
      key: 'status',
      label: `Status: ${statusLabel}`,
      clearPatch: { status: '', statusObj: null },
    });
  }

  if (!excludeArchived && filters.archived && filters.archived !== 'false') {
    const viewLabel = filters.archivedObj?.label
      || ArchiveViewOptions.find((item) => item.value === filters.archived)?.label
      || 'Archived';
    chips.push({
      key: 'archived',
      label: `View: ${viewLabel}`,
      clearPatch: {
        archived: 'false',
        archivedObj: ArchiveViewOptions[0],
      },
    });
  }

  return chips;
}

export function countActiveFilters(filters = {}, options = {}) {
  return getActiveFilterChips(filters, options).length;
}

export function getLoggedInOrganisationScope(user) {
  const organisation = user?.profile?.organisation ?? null;
  const organisationId = organisation?.organisation_id ?? '';
  return {
    isSuperUser: Boolean(user?.is_superuser),
    organisation,
    organisationId,
  };
}

export function lockTemplateOrganisationForUser(filters = {}, user) {
  const { isSuperUser, organisation, organisationId } = getLoggedInOrganisationScope(user);
  if (isSuperUser || !organisationId) {
    return { ...filters };
  }
  return {
    ...filters,
    organisation: organisationId,
    organisationObj: organisation,
  };
}

export function defaultTemplateOrganisation(filters = {}, user) {
  if (filters.organisation) return { ...filters };
  const { organisation, organisationId } = getLoggedInOrganisationScope(user);
  if (!organisationId) return { ...filters };
  return {
    ...filters,
    organisation: organisationId,
    organisationObj: organisation,
  };
}

export function applyTemplateOrganisationScope(filters = {}, user) {
  return lockTemplateOrganisationForUser(
    defaultTemplateOrganisation(filters, user),
    user
  );
}
